import React, { useEffect, useMemo, useState } from "react";
import { tcknState, taxIdState } from "../utils/identity";
import { postalValid } from "../utils/shipping";
import { firmService, districtService, taxOfficeService } from "../services/dataService";
import { Firm, FirmType, InvoiceType, City, District, TaxOffice } from "../types";
import { usePermissions } from "../context/PermissionContext";
import LookupField from "./LookupField";
import "../pages/PurchaseOrders.css";

interface Props {
  /** Düzenlenen firma; boşsa yeni kayıt. */
  firm?: Firm | null;
  invoiceTypes: InvoiceType[];
  cities: City[];
  onClose: () => void;
  onSaved: () => void;
  /** Posta kodu ve e-İrsaliye etiketi alanları gösterilsin mi (varsayılan evet). API bu alanları tanımıyorsa (e-İrsaliye
   *  arka ucu yayınlanmamışsa) çağıran false verir; aksi halde girilen değer kayıtta sessizce kaybolurdu. */
  despatchFields?: boolean;
}

interface FirmFormData {
  firmCode: string;
  firmName: string;
  firmType: string;
  description: string;
  address: string;
  cityId: string;
  districtId: string;
  taxOfficeId: string;
  countryCode: string;
  countryName: string;
  contactPerson: string;
  phone: string;
  email: string;
  notificationEmail: string;
  taxNumber: string;
  tckn: string;
  firstName: string;
  lastName: string;
  invoiceTypeId: string;
  postalCode: string;
  despatchAlias: string;
}

const empty: FirmFormData = {
  firmCode: "", firmName: "", firmType: "0", description: "", address: "",
  cityId: "", districtId: "", taxOfficeId: "", countryCode: "TR", countryName: "Türkiye",
  contactPerson: "", phone: "", email: "", notificationEmail: "",
  taxNumber: "", tckn: "", firstName: "", lastName: "", invoiceTypeId: "",
  postalCode: "", despatchAlias: "",
};

const fromFirm = (firm: Firm): FirmFormData => ({
  firmCode: firm.firmCode,
  firmName: firm.firmName,
  firmType: firm.firmType?.toString() || "0",
  description: firm.description || "",
  address: firm.address || "",
  cityId: firm.cityId?.toString() || "",
  districtId: firm.districtId?.toString() || "",
  taxOfficeId: firm.taxOfficeId?.toString() || "",
  countryCode: firm.countryCode || "TR",
  countryName: firm.countryName || "Türkiye",
  contactPerson: firm.contactPerson || "",
  phone: firm.phone || "",
  email: firm.email || "",
  notificationEmail: firm.notificationEmail || "",
  taxNumber: firm.taxNumber || "",
  tckn: firm.tckn || "",
  firstName: firm.firstName || "",
  lastName: firm.lastName || "",
  invoiceTypeId: firm.invoiceTypeId?.toString() || "",
  postalCode: firm.postalCode || "",
  despatchAlias: firm.despatchAlias || "",
});

/** Sunucu hatasını okunur metne çevirir (eski AlertModal metniyle birebir). */
const firmErr = (error: any, fallback: string): string => {
  if (error?.response?.data) {
    const d = error.response.data;
    if (typeof d === "string") return d;
    if (d.title || d.detail) return d.title || d.detail;
    if (d.errors) {
      return Object.entries(d.errors)
        .map(([field, messages]: [string, any]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
        .join("\n");
    }
  }
  return error?.message || fallback;
};

const closeIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>;

const FirmModal: React.FC<Props> = ({ firm, invoiceTypes, cities, onClose, onSaved, despatchFields = true }) => {
  const { hasModule } = usePermissions();
  const [f, setF] = useState<FirmFormData>(firm ? fromFirm(firm) : empty);
  const [errors, setErrors] = useState<Partial<FirmFormData>>({});
  const [cityText, setCityText] = useState(firm?.city?.cityName || "");
  const [districtText, setDistrictText] = useState(firm?.district?.districtName || "");
  const [districts, setDistricts] = useState<District[]>([]);
  const [taxOffices, setTaxOffices] = useState<TaxOffice[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Esc kapatır (kayıt sırasında değil; açık bir arama listesi Esc'i kendisi tüketir)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || saving) return;
      if ((e.target as HTMLElement | null)?.closest?.('[data-lookup-open="true"]')) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, saving]);

  // Düzenlemede firmanın ilçeleri ve vergi daireleri baştan yüklenir
  const initialCityId = firm?.cityId;
  useEffect(() => {
    if (!initialCityId) return;
    const id = initialCityId.toString();
    districtService.getDistrictsByCity(id).then(setDistricts).catch((e) => console.error("❌ İlçeler yüklenirken hata:", e));
    taxOfficeService.getTaxOfficesByCity(id).then(setTaxOffices).catch((e) => console.error("❌ Vergi daireleri yüklenirken hata:", e));
  }, [initialCityId]);

  const set = (k: keyof FirmFormData, v: string) => {
    setF((p) => ({ ...p, [k]: v }));
    setError("");
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleCityChange = (cityId: string) => {
    setF((p) => ({ ...p, cityId, districtId: "", taxOfficeId: "" }));
    setDistrictText("");
    setError("");
    if (cityId) {
      districtService.getDistrictsByCity(cityId).then(setDistricts).catch((e) => console.error("❌ İlçeler yüklenirken hata:", e));
      taxOfficeService.getTaxOfficesByCity(cityId).then(setTaxOffices).catch((e) => console.error("❌ Vergi daireleri yüklenirken hata:", e));
    } else {
      setDistricts([]);
      setTaxOffices([]);
    }
  };

  const cityOptions = useMemo(() => cities.map((c) => ({ id: c.cityId.toString(), label: c.cityName || "" })), [cities]);
  const districtOptions = useMemo(() => districts.map((d) => ({ id: d.districtId.toString(), label: d.districtName || "" })), [districts]);

  const vkn = taxIdState(f.taxNumber);
  const tc = tcknState(f.tckn);
  const isLegal = f.firmType === "0";

  const validate = (): boolean => {
    const next: Partial<FirmFormData> = {};
    if (!f.firmCode.trim()) next.firmCode = "Firma kodu zorunludur";
    if (!f.firmName.trim()) next.firmName = "Firma adı zorunludur";
    if (tc === "incomplete") next.tckn = "TC kimlik numarası 11 hane olmalı";
    if (tc === "invalid") next.tckn = "TC kimlik numarası geçersiz; son haneleri kontrol edin";
    if (vkn === "incomplete") next.taxNumber = "Vergi numarası 10 hane (şahıs için TCKN 11 hane) olmalı";
    if (vkn === "invalid") next.taxNumber = "Vergi numarası geçersiz; haneleri kontrol edin";
    if (f.postalCode.trim() && !postalValid(f.postalCode)) next.postalCode = "Posta kodu 5 haneli olmalı ve 01–81 il koduyla başlamalı (ör. 34200)";
    if (f.despatchAlias.trim() && !/^urn:mail:\S+$/i.test(f.despatchAlias.trim())) next.despatchAlias = "Etiket 'urn:mail:…' biçiminde olmalı (ör. urn:mail:irsaliyepk@firma.com.tr)";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    if (!validate()) return;
    setSaving(true);
    setError("");
    try {
      // Boş string'leri null'a çevir, kimlikleri tam sayıya dönüştür
      const firmData: any = {
        firmCode: f.firmCode,
        firmName: f.firmName,
        firmType: parseInt(f.firmType) as FirmType,
        description: f.description || null,
        address: f.address || null,
        cityId: f.cityId ? parseInt(f.cityId) : null,
        districtId: f.districtId ? parseInt(f.districtId) : null,
        taxOfficeId: f.taxOfficeId ? parseInt(f.taxOfficeId) : null,
        countryCode: f.countryCode || null,
        countryName: f.countryName || null,
        contactPerson: f.contactPerson || null,
        phone: f.phone || null,
        email: f.email || null,
        notificationEmail: f.notificationEmail || null,
        taxNumber: f.taxNumber || null,
        tckn: f.tckn || null,
        firstName: f.firstName || null,
        lastName: f.lastName || null,
        invoiceTypeId: f.invoiceTypeId ? parseInt(f.invoiceTypeId) : null,
        // Sunucu güncellemede ikisini de yazar: alan gösterilmese de kayıtlı değer geri gönderilir
        postalCode: f.postalCode.trim() || null,
        despatchAlias: f.despatchAlias.trim() || null,
      };
      if (firm) await firmService.updateFirm(firm.firmId, { ...firmData, firmId: firm.firmId });
      else await firmService.createFirm(firmData);
      onSaved();
      onClose();
    } catch (err: any) {
      console.error("❌ Firma kaydedilirken hata:", err);
      setError(firmErr(err, "Firma kaydedilirken bir hata oluştu."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="po-firm-title" onSubmit={save}>
        <div className="po-modal-head">
          <div><span className="po-kicker">Firma kartı</span><h3 id="po-firm-title">{firm ? firm.firmName : "Yeni firma"}</h3></div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{closeIcon}</button>
        </div>
        <div className="po-modal-body">
          {error && <div className="po-note err" role="alert" style={{ whiteSpace: "pre-line" }}>{error}</div>}

          <div className="po-form-sec"><h4>Temel bilgiler</h4>
            <div className="po-grid2">
              <label className="po-field"><span>Firma kodu<em>zorunlu</em></span>
                <input value={f.firmCode} autoFocus={!firm} placeholder="Örn: FRM001" className={errors.firmCode ? "is-invalid" : ""} onChange={(e) => set("firmCode", e.target.value)} />
                {errors.firmCode && <span className="po-fielderr">{errors.firmCode}</span>}
              </label>
              <label className="po-field"><span>Firma adı<em>zorunlu</em></span>
                <input value={f.firmName} autoFocus={!!firm} placeholder="Firma adını girin" className={errors.firmName ? "is-invalid" : ""} onChange={(e) => set("firmName", e.target.value)} />
                {errors.firmName && <span className="po-fielderr">{errors.firmName}</span>}
              </label>
              <div className="po-field"><span>Firma tipi</span>
                <div className="po-seg" role="radiogroup" aria-label="Firma tipi">
                  <button type="button" role="radio" aria-checked={isLegal} className={isLegal ? "on" : ""} onClick={() => set("firmType", "0")}>Tüzel kişi</button>
                  <button type="button" role="radio" aria-checked={!isLegal} className={!isLegal ? "on" : ""} onClick={() => set("firmType", "1")}>Gerçek kişi</button>
                </div>
              </div>
              <label className="po-field"><span>Fatura tipi</span>
                <select className="po-select" value={f.invoiceTypeId} onChange={(e) => set("invoiceTypeId", e.target.value)}>
                  <option value="">Fatura tipi seçin</option>
                  {invoiceTypes.map((t) => (
                    <option key={t.invoiceTypeId} value={t.invoiceTypeId}>{t.name}{t.description ? ` - ${t.description}` : ""}</option>
                  ))}
                </select>
              </label>
              <label className="po-field span2"><span>Açıklama</span>
                <textarea rows={2} value={f.description} placeholder="Firma hakkında açıklama" onChange={(e) => set("description", e.target.value)} />
              </label>
            </div>
          </div>

          {/* Vergi bilgileri yalnızca tüzel kişide; gerçek kişinin değerleri durumda korunur */}
          {isLegal && (
            <div className="po-form-sec"><h4>Vergi bilgileri</h4>
              <div className="po-grid2">
                <label className="po-field"><span>Vergi numarası{vkn === "invalid" && <em className="err">geçersiz</em>}{vkn === "ok" && <em className="ok">doğrulandı</em>}</span>
                  <input inputMode="numeric" maxLength={10} placeholder="Vergi numarası (10 hane)" className={errors.taxNumber || vkn === "invalid" ? "is-invalid" : ""} value={f.taxNumber} onChange={(e) => set("taxNumber", e.target.value.replace(/\D/g, ""))} />
                  {errors.taxNumber && <span className="po-fielderr">{errors.taxNumber}</span>}
                </label>
              </div>
            </div>
          )}

          {!isLegal && (
            <div className="po-form-sec"><h4>Kimlik bilgileri</h4>
              <div className="po-grid2">
                <label className="po-field span2"><span>TC kimlik numarası{tc === "invalid" && <em className="err">geçersiz</em>}{tc === "ok" && <em className="ok">doğrulandı</em>}</span>
                  <input inputMode="numeric" maxLength={11} placeholder="TC Kimlik No (11 hane)" className={errors.tckn || tc === "invalid" ? "is-invalid" : ""} value={f.tckn} onChange={(e) => set("tckn", e.target.value.replace(/\D/g, ""))} />
                  {errors.tckn && <span className="po-fielderr">{errors.tckn}</span>}
                </label>
                <label className="po-field"><span>Ad</span><input value={f.firstName} placeholder="Ad" onChange={(e) => set("firstName", e.target.value)} /></label>
                <label className="po-field"><span>Soyad</span><input value={f.lastName} placeholder="Soyad" onChange={(e) => set("lastName", e.target.value)} /></label>
              </div>
            </div>
          )}

          <div className="po-form-sec"><h4>İletişim</h4>
            <div className="po-grid2">
              <label className="po-field"><span>Yetkili kişi</span><input value={f.contactPerson} placeholder="Yetkili adı" onChange={(e) => set("contactPerson", e.target.value)} /></label>
              <label className="po-field"><span>Telefon</span><input type="tel" value={f.phone} placeholder="05XX XXX XX XX" onChange={(e) => set("phone", e.target.value)} /></label>
              <label className="po-field"><span>E-posta</span>
                <input type="email" value={f.email} placeholder="firma@example.com" onChange={(e) => set("email", e.target.value)} />
                <span className="po-fieldnote">E-Fatura / E-Arşiv gönderiminde zorunludur; alıcı GİB mükellefi değilse belge bu adrese iletilir.</span>
              </label>
              <label className="po-field"><span>Bildirim e-postası</span>
                <input type="email" value={f.notificationEmail} placeholder="siparis@example.com" onChange={(e) => set("notificationEmail", e.target.value)} />
                <span className="po-fieldnote">Sipariş tamamlandı ve sevkiyat yola çıktı bilgileri bu adrese gönderilir. Boşsa müşteriye bildirim gitmez; fatura adresine düşülmez.</span>
              </label>
            </div>
          </div>

          <div className="po-form-sec"><h4>Adres</h4>
            <div className="po-grid2">
              <label className="po-field span2"><span>Adres</span>
                <textarea rows={2} maxLength={500} value={f.address} placeholder="Sokak, mahalle, bina no vb." onChange={(e) => set("address", e.target.value)} />
              </label>
              <LookupField
                label="İl"
                options={cityOptions}
                value={f.cityId}
                text={cityText}
                onTextChange={setCityText}
                onChange={(id) => { if (id !== f.cityId) handleCityChange(id); }}
                placeholder="İl arayın…"
              />
              <LookupField
                label="İlçe"
                options={districtOptions}
                value={f.districtId}
                text={districtText}
                onTextChange={setDistrictText}
                onChange={(id) => set("districtId", id)}
                disabled={!f.cityId}
                placeholder={f.cityId ? "İlçe arayın…" : "Önce il seçin"}
              />
              {isLegal && (
                <label className="po-field span2"><span>Vergi dairesi</span>
                  <select className="po-select" value={f.taxOfficeId} disabled={!f.cityId} onChange={(e) => set("taxOfficeId", e.target.value)}>
                    <option value="">{f.cityId ? "Vergi dairesi seçin" : "Önce il seçin"}</option>
                    {taxOffices.map((o) => <option key={o.taxOfficeId} value={o.taxOfficeId}>{o.name}</option>)}
                  </select>
                </label>
              )}
              {despatchFields && (
              <label className="po-field"><span>Posta kodu{hasModule("shipping") && <em>e-İrsaliye teslimat adresi</em>}</span>
                <input inputMode="numeric" maxLength={5} placeholder="34200" className={errors.postalCode ? "is-invalid" : ""} value={f.postalCode} onChange={(e) => set("postalCode", e.target.value.replace(/\D/g, ""))} />
                {errors.postalCode && <span className="po-fielderr">{errors.postalCode}</span>}
              </label>
              )}
              <label className="po-field"><span>Ülke kodu</span><input value={f.countryCode} maxLength={3} placeholder="TR" onChange={(e) => set("countryCode", e.target.value)} /></label>
              <label className="po-field span2"><span>Ülke</span><input value={f.countryName} placeholder="Türkiye" onChange={(e) => set("countryName", e.target.value)} /></label>
            </div>
          </div>

          {despatchFields && hasModule("shipping") && (
            <div className="po-form-sec"><h4>e-İrsaliye</h4>
              <div className="po-grid2">
                <label className="po-field span2"><span>Alıcı e-İrsaliye etiketi<em>isteğe bağlı</em></span>
                  <input value={f.despatchAlias} maxLength={100} placeholder="urn:mail:irsaliyepk@firma.com.tr" className={errors.despatchAlias ? "is-invalid" : ""} onChange={(e) => set("despatchAlias", e.target.value)} />
                  {errors.despatchAlias ? <span className="po-fielderr">{errors.despatchAlias}</span>
                    : <span className="po-fieldnote">Yalnız firmanın birden çok e-İrsaliye posta kutusu varsa gerekir; sevk penceresinde seçilen etiket buraya yazılır.</span>}
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="po-modal-foot between">
          <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button type="submit" className="po-btn primary" disabled={saving}>{saving ? "Kaydediliyor…" : firm ? "Kaydet" : "Firmayı oluştur"}</button>
        </div>
      </form>
    </div>
  );
};

export default FirmModal;
